import type { Vault } from '@/types/vault'
import { getDefiSdk } from '@/utils/defiSdk'
import { queryOptions, useQuery } from '@tanstack/react-query'
import { SECOND_IN_MILLISECONDS } from '@vaultsfyi/common/constants'
import type { Address } from 'viem'

export const actionsQueryKey = ['actions'] as const

export type Action =
  | 'deposit'
  | 'redeem'
  | 'request-deposit'
  | 'claim-deposit'
  | 'request-redeem'
  | 'claim-redeem'
  | 'claim-rewards'
  | 'start-redeem-cooldown'

interface ActionsArgs {
  address: Address | undefined
  action: Action
  vault: Vault
  assetAddress?: Address | undefined
  amount: string
  all?: boolean
  ignoreAmount?: boolean
  simulate?: boolean
  enabled?: boolean
}

const actionsQueryOptions = ({
  address,
  action,
  vault,
  assetAddress,
  amount,
  all,
  ignoreAmount,
  simulate,
}: ActionsArgs) =>
  queryOptions({
    queryKey: [
      ...actionsQueryKey,
      {
        assetAddress: assetAddress ?? vault.asset.address,
        network: vault.network.name,
        vaultId: vault.vaultId,
        userAddress: address,
        simulate,
        amount,
        all,
        action,
        ignoreAmount,
      },
    ] as const,
    async queryFn({ queryKey }) {
      const { action, userAddress, network, vaultId, simulate, assetAddress, amount, all, ignoreAmount } = queryKey[1]

      if (!userAddress) return null
      if (!action) return null
      if (!ignoreAmount && amount === '0') return null

      const defiSdk = getDefiSdk()

      return await defiSdk.getActions({
        path: {
          action,
          userAddress,
          network,
          vaultId,
        },
        query: {
          simulate,
          assetAddress,
          amount,
          all,
        },
      })
    },
    enabled: !!address && !!action && (ignoreAmount ? true : amount !== '0'),
    retry: 1,
    staleTime: 30 * SECOND_IN_MILLISECONDS,
    refetchOnWindowFocus: true,
  })

export function useActionsQuery({
  address,
  action,
  vault,
  assetAddress,
  amount,
  all,
  ignoreAmount,
  simulate,
  enabled = true,
}: ActionsArgs) {
  const options = actionsQueryOptions({
    address,
    action,
    vault,
    assetAddress,
    amount,
    all,
    ignoreAmount,
    simulate,
  })

  const { data, isFetching } = useQuery({
    ...options,
    enabled: options.enabled && enabled,
  })

  return { data, isFetching }
}

export type Actions = ReturnType<typeof useActionsQuery>['data']
